"use client"

/*
==================================================
SHIPPING STEP

Функция:
шаг выбора доставки внутри mobile cart modal

Родитель:
cart-mobile-body.tsx

Дочерние элементы:
RadioGroup
ErrorMessage

Использует:
setShippingMethod
calculatePriceForShippingOption

Примечания:
временная оплата из shipping step удалена
кнопка Next в footer должна переводить на payment step
==================================================
*/

import { Radio, RadioGroup } from "@headlessui/react"
import { useEffect, useMemo, useState } from "react"

import { setShippingMethod } from "@lib/data/cart"
import { calculatePriceForShippingOption } from "@lib/data/fulfillment"
import { convertToLocale } from "@lib/util/money"

import { Loader } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import { clx } from "@medusajs/ui"

import ErrorMessage from "@modules/checkout/components/error-message"
import MedusaRadio from "@modules/common/components/radio"

type ShippingStepProps = {
  cart?: HttpTypes.StoreCart | null
  availableShippingMethods?: HttpTypes.StoreCartShippingOption[] | null
}

export default function ShippingStep({
  cart,
  availableShippingMethods = [],
}: ShippingStepProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingPrices, setIsLoadingPrices] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [calculatedPricesMap, setCalculatedPricesMap] = useState<
    Record<string, number>
  >({})

  const [shippingMethodId, setShippingMethodId] = useState<string | null>(
    cart?.shipping_methods?.at(-1)?.shipping_option_id || null
  )

  const shippingMethods = useMemo(() => {
    return availableShippingMethods.filter(
      (sm) => sm.service_zone?.fulfillment_set?.type !== "pickup"
    )
  }, [availableShippingMethods])

  useEffect(() => {
    setShippingMethodId(
      cart?.shipping_methods?.at(-1)?.shipping_option_id || null
    )
  }, [cart?.shipping_methods])

  useEffect(() => {
    let isMounted = true

    async function loadPrices() {
      if (!cart?.id || !shippingMethods.length) {
        if (isMounted) {
          setCalculatedPricesMap({})
          setIsLoadingPrices(false)
        }
        return
      }

      setIsLoadingPrices(true)

      const calculatedOnly = shippingMethods.filter(
        (sm) => sm.price_type === "calculated"
      )

      if (!calculatedOnly.length) {
        if (isMounted) {
          setCalculatedPricesMap({})
          setIsLoadingPrices(false)
        }
        return
      }

      const results = await Promise.allSettled(
        calculatedOnly.map((sm) => calculatePriceForShippingOption(sm.id, cart.id))
      )

      if (!isMounted) return

      const pricesMap: Record<string, number> = {}

      results.forEach((res) => {
        if (res.status === "fulfilled" && res.value?.id) {
          pricesMap[res.value.id] = res.value.amount
        }
      })

      setCalculatedPricesMap(pricesMap)
      setIsLoadingPrices(false)
    }

    loadPrices()

    return () => {
      isMounted = false
    }
  }, [shippingMethods, cart?.id])

  useEffect(() => {
    if (!cart?.id) return
    if (!shippingMethods.length) return
    if (shippingMethodId) return

    const firstAvailable = shippingMethods.find(
      (option) => !option.insufficient_inventory
    )
    if (!firstAvailable) return

    const autoSelect = async () => {
      setError(null)
      setIsLoading(true)
      setShippingMethodId(firstAvailable.id)

      try {
        await setShippingMethod({
          cartId: cart.id,
          shippingMethodId: firstAvailable.id,
        })
      } catch (err: any) {
        setShippingMethodId(null)
        setError(err?.message || "Failed to set shipping method.")
      } finally {
        setIsLoading(false)
      }
    }

    autoSelect()
  }, [shippingMethods, shippingMethodId, cart?.id])

  const handleSetShippingMethod = async (id: string) => {
    if (!cart?.id) return

    setError(null)

    let previousId: string | null = null
    setIsLoading(true)

    setShippingMethodId((prev) => {
      previousId = prev
      return id
    })

    try {
      await setShippingMethod({
        cartId: cart.id,
        shippingMethodId: id,
      })
    } catch (err: any) {
      setShippingMethodId(previousId)
      setError(err?.message || "Failed to set shipping method.")
    } finally {
      setIsLoading(false)
    }
  }

  if (!cart) return null

  return (
    <div className="rounded-t-2xl border-x border-t border-slate-200 bg-white px-4 py-6 shadow-[0_4px_12px_rgba(15,23,42,0.2)]">
      <div className="mb-1 text-[1.05rem] font-extrabold tracking-[0.04em] text-slate-900">
        Shipping method
      </div>

      <div className="mb-4 text-sm leading-6 text-slate-600">
        Choose how you would like your order delivered
      </div>

      {!shippingMethods.length ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">
          Shipping options are not available yet.
        </div>
      ) : (
        <RadioGroup
          value={shippingMethodId}
          onChange={(value) => {
            if (value) {
              handleSetShippingMethod(value)
            }
          }}
        >
          <div className="space-y-2">
            {shippingMethods.map((option) => {
              const isDisabled =
                option.price_type === "calculated" &&
                !isLoadingPrices &&
                typeof calculatedPricesMap[option.id] !== "number"

              const displayPrice =
                option.price_type === "flat"
                  ? convertToLocale({
                      amount: option.amount || 0,
                      currency_code: cart.currency_code,
                    })
                  : typeof calculatedPricesMap[option.id] === "number"
                  ? convertToLocale({
                      amount: calculatedPricesMap[option.id],
                      currency_code: cart.currency_code,
                    })
                  : isLoadingPrices
                  ? null
                  : "-"

              return (
                <Radio
                  key={option.id}
                  value={option.id}
                  disabled={isDisabled}
                  className={clx(
                    "flex items-center justify-between rounded-xl border px-4 py-4 text-sm transition",
                    {
                      "border-blue-500 bg-blue-50/60": option.id === shippingMethodId,
                      "border-slate-200 bg-white": option.id !== shippingMethodId,
                      "cursor-pointer hover:border-slate-300": !isDisabled,
                      "cursor-not-allowed opacity-60": isDisabled,
                    }
                  )}
                >
                  <div className="flex items-center gap-x-4">
                    <MedusaRadio checked={option.id === shippingMethodId} />
                    <span className="font-medium text-slate-900">{option.name}</span>
                  </div>

                  <span className="text-slate-700">
                    {displayPrice === null ? <Loader /> : displayPrice}
                  </span>
                </Radio>
              )
            })}
          </div>
        </RadioGroup>
      )}

      <div className="mt-4">
        <ErrorMessage error={error} data-testid="delivery-option-error-message" />
      </div>

      {isLoading && (
        <div className="mt-3 text-sm font-medium text-slate-500">
          Saving shipping method...
        </div>
      )}
    </div>
  )
}
